import { Link } from '@tanstack/react-router';
import { flexRender, useReactTable } from '@tanstack/react-table';
import {
  type ColumnDef,
  getCoreRowModel,
  getSortedRowModel,
  type SortingState,
} from '@tanstack/table-core';
import { ArrowUpDown, GlobeIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { type HostedResource, type HostResource } from '@/lib/queries/resource';

import ResourceHeader from './ResourceHeader';

const getColumns = (): ColumnDef<HostedResource>[] => {
  return [
    {
      accessorKey: 'service',
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="pl-0"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
        >
          <FormattedMessage id="resource.host.hostedResources.service" />
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => {
        const { service } = row.original;

        return service;
      },
    },
    {
      accessorKey: 'resourceType',
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="pl-0"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
        >
          <FormattedMessage id="resource.host.hostedResources.resourceType" />
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => {
        const { resourceType } = row.original;

        return resourceType;
      },
    },
    {
      accessorKey: 'resourceId',
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="pl-0"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
        >
          <FormattedMessage id="resource.host.hostedResources.resourceId" />
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => {
        const { resourceId } = row.original;
        const { hashedID } = row.original;

        return (
          <Link
            to="/resource/$resourceId"
            params={{ resourceId: hashedID }}
            className="underline hover:text-blue-500"
          >
            {resourceId}
          </Link>
        );
      },
    },
  ];
};

interface ResourceProps {
  resource: HostResource;
}

export const Host = ({ resource }: ResourceProps): JSX.Element => {
  const columns = useMemo(() => getColumns(), []);
  const [sorting, setSorting] = useState<SortingState>([]);

  const table = useReactTable({
    data: resource.resources,
    columns,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    state: {
      sorting,
    },
  });

  return (
    <>
      <ResourceHeader
        title={resource.hostName.toUpperCase()}
        subtitle={resource.hostType}
        Icon={<GlobeIcon className="text-blue-500 h-16 w-16" />}
      />
      <h2 className="text-2xl mt-8 mb-6">
        <FormattedMessage id="resource.host.hostedResources.title" />
      </h2>
      <div className="gap-10 mb-6 w-full">
        <div className="w-full">
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  {table.getFlatHeaders().map(header => {
                    return (
                      <TableHead key={header.id}>
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                      </TableHead>
                    );
                  })}
                </TableRow>
              </TableHeader>
              <TableBody>
                {table.getRowModel().rows.length > 0 ? (
                  table.getRowModel().rows.map(row => (
                    <TableRow key={row.id}>
                      {row.getVisibleCells().map(cell => (
                        <TableCell key={cell.id}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      className="h-24 text-center"
                    >
                      <FormattedMessage id="global.noResults" />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
    </>
  );
};
